import { ApplicationStatus } from "@eip/shared/types/application.types";

// --- Types ---

export type RoadmapCardStatus = "complete" | "current" | "locked" | "unsuccessful";

export type RoadmapCardDef = {
  status: RoadmapCardStatus;
  /** textData key resolved from textPrefix, e.g. "Roadmap1" */
  titleKey: string;
  subtitleKey?: string;
  /** If set, wraps card in a clickable button navigating to this route */
  clickRoute?: string;
  /** If true + intake closed, shows "locked" instead of clickRoute */
  intakeGated?: boolean;
  /** Override textPrefix for this card only (e.g. pull from a different dashboard's text block) */
  textPrefixOverride?: string;
};

export type GenericDashboardDef = {
  kind: "generic";
  /** Key into textData.Pages.Dashboards.FounderDashboards */
  textPrefix: string;
  /** If set, renders <h4> portal title from this key */
  portalTitleKey?: string;
  roadmapCards: RoadmapCardDef[];
  /** Omit the roadmap card's <h3> title (IO/NF approved layout) */
  hideRoadmapTitle?: boolean;
  /** Override the roadmap card's width/flex classes (default "flex-1 min-w-0") */
  roadmapSizingClassName?: string;
  /** Header rendered inside the roadmap card above the cards: description
   *  text (keys concatenated, no separator) + optional bolded submission
   *  date with a literal suffix (IO/NF approved layout). Requires
   *  fetchLatestApp when showSubmissionDate is set. */
  roadmapHeader?: { descriptionKeys: string[]; showSubmissionDate?: boolean; dateSuffix?: string };
  /** Suppress the entire nextStep section (no title/description/button) */
  hideNextStep?: boolean;
  /** Where program details renders: bottom strip (default) or stacked in the
   *  right column under education (IO/NF approved layout) */
  programDetailsPlacement?: "footer" | "sidebar";
  // NextStep section
  showIcon?: boolean;
  descriptionKeys: string[];
  bulletKeys?: string[];
  /** Keys for "in touch" text lines, rendered as <span> with <br> between */
  inTouchKeys?: string[];
  /** Override textPrefix for NextStep title only */
  nextStepTitlePrefix?: string;
  /** Override the key used for NextStep title (default: "NextStepTitle") */
  nextStepTitleKey?: string;
  // Button
  buttonKey: string;
  /** Override textPrefix for button text */
  buttonTextPrefix?: string;
  buttonRoute?: string;
  buttonDisabled?: boolean;
  /** Alternate button key when midApplication is true */
  buttonKeyMidApp?: string;
  /** Route to use when midApplication is true (defaults to buttonRoute) */
  buttonRouteMidApp?: string;
  // Optional behaviors
  fetchLatestApp?: boolean;
  showSubmissionDate?: boolean;
  showReasonNote?: boolean;
  /** textData key for label above reasonNote (e.g. "ReasonNoteLabel") */
  reasonNoteLabelKey?: string;
  /** textData key rendered when showReasonNote is set but the application has
   *  no reasonNote — keeps the card body from collapsing to title+button */
  reasonNoteFallbackKey?: string;
  /** Whether to show the intake-closed Alert instead of button when intake is closed */
  intakeGatedButton?: boolean;
  /** Rejected dashboards: read latestApp.reapplyEligibility — show the eligible
   *  date while in cooldown, swap to an intake-gated "Apply again" CTA once past it */
  reapplyAware?: boolean;
  // Education/ProgramDetails
  education?: { textPrefix: string; bulletTextPrefix?: string; buttonTextPrefix?: string };
  programDetailsTextPrefix?: string;
};

export type StandaloneDashboardDef = {
  kind: "standalone";
  component: "PreSowDashboard" | "SelectSpfFounderDashboard";
};

export type DashboardDef = GenericDashboardDef | StandaloneDashboardDef;

type StatusMapping = {
  statuses: ApplicationStatus[];
  config: DashboardDef;
  /** If set, this config only applies for the given region condition */
  regionCondition?: "ioNorthForge" | "otherRegions" | "mvScope";
};

// --- Config factories ---
// The MV (Market Validation) dashboards are region-parameterized variants of
// the full-portal ones: same structure, different textPrefix + a shorter
// roadmap (and, where noted, shorter copy). Factories keep each pair as ONE
// definition. Outputs are frozen — SUBMITTED_CONFIG is shared by two mappings
// and the resolver fallback, so accidental mutation would leak across statuses.

function makeSubmittedConfig(params: {
  textPrefix: string;
  roadmapCards: RoadmapCardDef[];
  descriptionKeys: string[];
}): GenericDashboardDef {
  return Object.freeze({
    kind: "generic" as const,
    textPrefix: params.textPrefix,
    portalTitleKey: "Portal",
    roadmapCards: params.roadmapCards,
    showIcon: true,
    descriptionKeys: params.descriptionKeys,
    inTouchKeys: ["InTouch"],
    buttonKey: "NextStepButton",
    buttonRoute: "/dashboard/profile",
    fetchLatestApp: true,
    showSubmissionDate: true,
    education: { textPrefix: params.textPrefix },
    programDetailsTextPrefix: params.textPrefix,
  });
}

function makeUnsubmittedConfig(params: {
  textPrefix: string;
  roadmapCards: RoadmapCardDef[];
  bulletKeys: string[];
}): GenericDashboardDef {
  return Object.freeze({
    kind: "generic" as const,
    textPrefix: params.textPrefix,
    portalTitleKey: "Portal",
    roadmapCards: params.roadmapCards,
    showIcon: true,
    descriptionKeys: ["NextStep"],
    bulletKeys: params.bulletKeys,
    buttonKey: "NextStepButton",
    buttonKeyMidApp: "NextStepButtonMidApp",
    buttonRoute: "/dashboard/application",
    buttonRouteMidApp: "/dashboard/profile",
    intakeGatedButton: true,
    education: { textPrefix: params.textPrefix },
    programDetailsTextPrefix: params.textPrefix,
  });
}

function makeRejectedConfig(params: {
  textPrefix: string;
  roadmapCards: RoadmapCardDef[];
}): GenericDashboardDef {
  return Object.freeze({
    kind: "generic" as const,
    textPrefix: params.textPrefix,
    roadmapCards: params.roadmapCards,
    descriptionKeys: ["NextStep"],
    buttonKey: "NextStepButton",
    buttonRoute: "/dashboard/help",
    fetchLatestApp: true,
    showReasonNote: true,
    reapplyAware: true,
    education: { textPrefix: params.textPrefix },
    programDetailsTextPrefix: params.textPrefix,
  });
}

/** The MV and full-portal edit-request dashboards differ ONLY in textPrefix. */
function makeEditRequestConfig(textPrefix: string): GenericDashboardDef {
  return Object.freeze({
    kind: "generic" as const,
    textPrefix,
    portalTitleKey: "ReapplyPortal",
    roadmapCards: [
      { status: "complete" as const, titleKey: "Roadmap1" },
      { status: "complete" as const, titleKey: "ReapplyRoadmap2" },
      { status: "current" as const, titleKey: "ReapplyRoadmap3", subtitleKey: "ReapplyRoadmap3Sub" },
    ],
    nextStepTitleKey: "ReapplyNextStepTitle",
    descriptionKeys: [],
    buttonKey: "ReapplyNextStepButton",
    buttonRoute: "/dashboard/profile",
    fetchLatestApp: true,
    showReasonNote: true,
    reasonNoteLabelKey: "EditRequestNoteLabel",
    reasonNoteFallbackKey: "EditRequestNoteFallback",
    education: { textPrefix },
    programDetailsTextPrefix: textPrefix,
  });
}

/**
 * IO/NorthForge approved dashboard — the former standalone
 * ApprovedFounderDashboard.tsx expressed as a generic config: no roadmap
 * title, submission-date header inside the card, no next-step section,
 * program details stacked in the right column. Text all comes from the
 * SubmittedFounderDashboard block (as the standalone's did). buttonKey is
 * required by the type but never rendered (hideNextStep).
 */
export const APPROVED_IO_NF_CONFIG: GenericDashboardDef = Object.freeze({
  kind: "generic" as const,
  textPrefix: "SubmittedFounderDashboard",
  portalTitleKey: "Portal",
  hideRoadmapTitle: true,
  roadmapSizingClassName: "w-full h-fit",
  roadmapHeader: {
    descriptionKeys: ["NextStep", "NextStep2"],
    showSubmissionDate: true,
    dateSuffix: "!",
  },
  roadmapCards: [{ status: "complete" as const, titleKey: "Roadmap2" }],
  hideNextStep: true,
  descriptionKeys: [],
  buttonKey: "NextStepButton",
  fetchLatestApp: true,
  education: { textPrefix: "SubmittedFounderDashboard" },
  programDetailsTextPrefix: "SubmittedFounderDashboard",
  programDetailsPlacement: "sidebar" as const,
});

const SUBMITTED_CONFIG = makeSubmittedConfig({
  textPrefix: "SubmittedFounderDashboard",
  roadmapCards: [
    { status: "complete", titleKey: "Roadmap1" },
    { status: "complete", titleKey: "Roadmap2" },
    { status: "locked", titleKey: "Roadmap3", subtitleKey: "Roadmap3Sub" },
    { status: "locked", titleKey: "Roadmap4", subtitleKey: "Roadmap4Sub" },
  ],
  descriptionKeys: ["NextStep", "NextStep2"],
});

const MV_SUBMITTED_CONFIG = makeSubmittedConfig({
  textPrefix: "MvSubmittedDashboard",
  roadmapCards: [
    { status: "complete", titleKey: "Roadmap1" },
    { status: "complete", titleKey: "Roadmap2" },
  ],
  descriptionKeys: ["NextStep"],
});

export const STATUS_MAPPINGS: StatusMapping[] = [
  // --- MV Scope: Unsubmitted ---
  {
    statuses: ["unsubmitted"],
    regionCondition: "mvScope",
    config: makeUnsubmittedConfig({
      textPrefix: "MvUnsubmittedDashboard",
      roadmapCards: [
        { status: "complete", titleKey: "Roadmap1" },
        { status: "current", titleKey: "Roadmap2", subtitleKey: "Roadmap2Sub", clickRoute: "/dashboard/application", intakeGated: true },
      ],
      bulletKeys: ["NextStepBullet1", "NextStepBullet2"],
    }),
  },

  // --- MV Scope: Submitted / In-Progress ---
  {
    statuses: [
      "pending", "submitted", "screened", "ddInProgress", "dueDiligence",
      "rejectedFlagged", "rejectedInProgress",
    ],
    regionCondition: "mvScope",
    config: MV_SUBMITTED_CONFIG,
  },

  // --- MV Scope: Approved ---
  {
    statuses: ["n/a", "approved"],
    regionCondition: "mvScope",
    config: {
      kind: "generic",
      textPrefix: "MvApprovedDashboard",
      portalTitleKey: "Portal",
      roadmapCards: [
        { status: "complete", titleKey: "Roadmap1" },
        { status: "complete", titleKey: "Roadmap2" },
      ],
      descriptionKeys: ["NextStep"],
      buttonKey: "NextStepButton",
      buttonRoute: "/dashboard/profile",
      education: { textPrefix: "MvApprovedDashboard" },
      programDetailsTextPrefix: "MvApprovedDashboard",
    },
  },

  // --- MV Scope: Rejected ---
  {
    statuses: ["rejectedReapply", "rejected"],
    regionCondition: "mvScope",
    config: makeRejectedConfig({
      textPrefix: "MvRejectedDashboard",
      roadmapCards: [
        { status: "complete", titleKey: "Roadmap1" },
        { status: "unsuccessful", titleKey: "Roadmap2" },
      ],
    }),
  },

  // --- MV Scope: Edit Request ---
  {
    statuses: ["pendingEditRequest"],
    regionCondition: "mvScope",
    config: makeEditRequestConfig("MvRejectedDashboard"),
  },

  // --- Unsubmitted ---
  {
    statuses: ["unsubmitted"],
    config: makeUnsubmittedConfig({
      textPrefix: "UnsubmittedFounderDashboard",
      roadmapCards: [
        { status: "complete", titleKey: "Roadmap1" },
        { status: "current", titleKey: "Roadmap2", subtitleKey: "Roadmap2Sub", clickRoute: "/dashboard/application", intakeGated: true },
        { status: "locked", titleKey: "Roadmap3", subtitleKey: "Roadmap3Sub" },
        { status: "locked", titleKey: "Roadmap4", subtitleKey: "Roadmap4Sub" },
      ],
      bulletKeys: ["NextStepBullet1", "NextStepBullet2", "NextStepBullet3"],
    }),
  },

  // --- Submitted / In-Progress (many statuses map here) ---
  {
    statuses: [
      "pending", "submitted", "screened", "ddInProgress", "dueDiligence",
      "awaitingIpe", "coiWithIpa", "growthCoachInvite", "growthCoachInProgress",
      "growthCoachRecommendation", "rejectedFlagged", "rejectedInProgress",
    ],
    config: SUBMITTED_CONFIG,
  },

  // --- Approved (IO/NorthForge: minimal approved dashboard) ---
  {
    statuses: ["n/a", "approved"],
    regionCondition: "ioNorthForge",
    config: APPROVED_IO_NF_CONFIG,
  },

  // --- Approved (other regions: full roadmap, same layout as Submitted) ---
  {
    statuses: ["n/a", "approved"],
    regionCondition: "otherRegions",
    config: SUBMITTED_CONFIG,
  },

  // --- Rejected ---
  {
    statuses: ["rejectedReapply", "rejected"],
    config: makeRejectedConfig({
      textPrefix: "RejectedFounderDashboard",
      roadmapCards: [
        { status: "complete", titleKey: "Roadmap1" },
        { status: "unsuccessful", titleKey: "Roadmap2" },
        { status: "locked", titleKey: "Roadmap3", subtitleKey: "Roadmap3Sub" },
        { status: "locked", titleKey: "Roadmap4", subtitleKey: "Roadmap4Sub" },
      ],
    }),
  },

  // --- Edit Request ---
  {
    statuses: ["pendingEditRequest"],
    config: makeEditRequestConfig("RejectedFounderDashboard"),
  },

  // --- Expert Invite ---
  {
    statuses: ["ipExpertInvite"],
    config: {
      kind: "generic",
      textPrefix: "ExpertInviteDashboard",
      portalTitleKey: "Portal",
      roadmapCards: [
        { status: "complete", titleKey: "Roadmap1" },
        { status: "complete", titleKey: "Roadmap2" },
        { status: "current", titleKey: "Roadmap3", subtitleKey: "Roadmap3Sub" },
        { status: "locked", titleKey: "Roadmap4", subtitleKey: "Roadmap4Sub" },
      ],
      showIcon: true,
      descriptionKeys: ["NextStep"],
      inTouchKeys: ["InTouch"],
      buttonKey: "NextStepButton",
      // Button opens modal — onButtonClick override in parent
      education: { textPrefix: "ExpertInviteDashboard", bulletTextPrefix: "SubmittedFounderDashboard", buttonTextPrefix: "UnsubmittedFounderDashboard" },
      programDetailsTextPrefix: "ExpertInviteDashboard",
    },
  },

  // --- Expert Met / In Progress ---
  {
    statuses: ["ipExpertInProgress", "ipeBoardReview", "ipExpertRecommendation"],
    config: {
      kind: "generic",
      textPrefix: "ExpertMetDashboard",
      portalTitleKey: "Portal",
      roadmapCards: [
        { status: "complete", titleKey: "Roadmap1" },
        { status: "complete", titleKey: "Roadmap2" },
        { status: "complete", titleKey: "Roadmap3" },
        { status: "locked", titleKey: "Roadmap4", subtitleKey: "Roadmap4Sub" },
      ],
      showIcon: true,
      descriptionKeys: ["NextStep", "NextStep2"],
      inTouchKeys: ["InTouch", "InTouch2"],
      buttonKey: "NextStepButton",
      buttonDisabled: true,
      education: { textPrefix: "ExpertMetDashboard", bulletTextPrefix: "SubmittedFounderDashboard", buttonTextPrefix: "UnsubmittedFounderDashboard" },
      programDetailsTextPrefix: "ExpertMetDashboard",
    },
  },

  // --- Select SPF (standalone — complex modal) ---
  // Deliberately NOT genericized: the entire founder-picks-SP flow is slated
  // for deletion once PROD validation confirms no real customers sit in
  // selectSpf/newSpfRequested — see docs/11-backlog/13-cleanup-selectspf-legacy-flow.md.
  {
    statuses: ["selectSpf"],
    config: { kind: "standalone", component: "SelectSpfFounderDashboard" },
  },

  // --- Pending SPF ---
  {
    statuses: ["newSpfRequested"],
    config: {
      kind: "generic",
      textPrefix: "PendingSpfDashboard",
      portalTitleKey: "Portal",
      roadmapCards: [
        { status: "complete", titleKey: "Roadmap2", textPrefixOverride: "ExpertMetDashboard" },
        { status: "complete", titleKey: "Roadmap3", textPrefixOverride: "ExpertMetDashboard" },
        { status: "complete", titleKey: "Roadmap3" },
        { status: "locked", titleKey: "Roadmap4", subtitleKey: "Roadmap4Sub" },
      ],
      showIcon: true,
      nextStepTitlePrefix: "ExpertMetDashboard",
      descriptionKeys: ["NextStep", "NextStep2"],
      inTouchKeys: ["InTouch", "InTouch2"],
      buttonKey: "NextStepButton",
      buttonTextPrefix: "ExpertMetDashboard",
      buttonDisabled: true,
      education: { textPrefix: "ExpertMetDashboard", bulletTextPrefix: "SubmittedFounderDashboard", buttonTextPrefix: "UnsubmittedFounderDashboard" },
      programDetailsTextPrefix: "ExpertMetDashboard",
    },
  },

  // --- SOW lifecycle (standalone — complex component) ---
  {
    statuses: [
      "sowRequested", "sowInProgress", "sowAwaitingApproval", "sowAwaitingAdminApproval",
      "sowReadyForAudit", "sowEditRequested", "sowAwaitingSignature", "sowApproved",
      "sowSigned", "retainerInProgress",
    ],
    config: { kind: "standalone", component: "PreSowDashboard" },
  },

  // --- Survey Phase (reuses Project Completed view — survey is external) ---
  {
    statuses: ["sowComplete", "surveySent", "surveyComplete"],
    config: {
      kind: "generic",
      textPrefix: "ProjectCompletedFounderDashboard",
      portalTitleKey: "Portal",
      roadmapCards: [
        { status: "complete", titleKey: "Roadmap1" },
        { status: "complete", titleKey: "Roadmap2" },
      ],
      descriptionKeys: ["NextStep"],
      buttonKey: "NextStepButton",
      intakeGatedButton: true,
      fetchLatestApp: true,
      education: { textPrefix: "RejectedFounderDashboard" },
      programDetailsTextPrefix: "RejectedFounderDashboard",
    },
  },

  // --- Project Completed ---
  {
    statuses: ["projectCompleted", "partnerOrgProjectCompleted", "closed"],
    config: {
      kind: "generic",
      textPrefix: "ProjectCompletedFounderDashboard",
      portalTitleKey: "Portal",
      roadmapCards: [
        { status: "complete", titleKey: "Roadmap1" },
        { status: "complete", titleKey: "Roadmap2" },
      ],
      descriptionKeys: ["NextStep"],
      buttonKey: "NextStepButton",
      // Button triggers reapply mutation — onButtonClick override in parent
      intakeGatedButton: true,
      fetchLatestApp: true,
      showReasonNote: true,
      reasonNoteLabelKey: "ReasonNoteLabel",
      education: { textPrefix: "RejectedFounderDashboard" },
      programDetailsTextPrefix: "RejectedFounderDashboard",
    },
  },
];

// --- Resolver ---

const IO_NF_REGIONS = new Set(["investOttawa", "northForge"]);

/**
 * Legacy status aliases — DB rows may still carry status values that were
 * removed by past cleanup commits. Map them to their modern equivalents
 * here so the resolver doesn't crash the founder dashboard.
 *
 * Cleanup history:
 * - sowAwaitingStartupApproval → removed in commit b2ee5f9e (2026-03-24).
 *   Modern equivalent: sowAwaitingApproval (same lifecycle bucket).
 *   Migration: scripts/migrateLegacyAppStatuses.ts
 */
const LEGACY_STATUS_ALIASES: Record<string, ApplicationStatus> = {
  sowAwaitingStartupApproval: "sowAwaitingApproval",
};

export function resolveConfig(
  status: ApplicationStatus,
  userRegion: string | null,
  isMvScope?: boolean
): DashboardDef {
  // Normalize legacy statuses to their modern equivalents before resolving.
  const aliased = LEGACY_STATUS_ALIASES[status as string];
  if (aliased) {
    // eslint-disable-next-line no-console
    console.warn(`[dashboardRouteConfig] Aliasing legacy status "${status}" → "${aliased}". Run scripts/migrateLegacyAppStatuses.ts to clean up the DB.`);
    status = aliased;
  }

  // MV scope takes highest priority
  if (isMvScope) {
    const mvMatch = STATUS_MAPPINGS.find(
      m => m.statuses.includes(status) && m.regionCondition === "mvScope"
    );
    if (mvMatch) return mvMatch.config;
  }

  const regionCondition = userRegion && IO_NF_REGIONS.has(userRegion)
    ? "ioNorthForge"
    : "otherRegions";

  // Then try region-specific mappings
  const regionMatch = STATUS_MAPPINGS.find(
    m => m.statuses.includes(status) && m.regionCondition === regionCondition
  );
  if (regionMatch) return regionMatch.config;

  // Then try universal mappings (no regionCondition)
  const universalMatch = STATUS_MAPPINGS.find(
    m => m.statuses.includes(status) && !m.regionCondition
  );
  if (universalMatch) return universalMatch.config;

  // Last-resort fallback: log loudly and route to the "submitted" dashboard.
  // We deliberately do NOT throw here — a single legacy/unknown status row
  // should not crash the entire founder dashboard for that user.
  // eslint-disable-next-line no-console
  console.error(`[dashboardRouteConfig] Unknown Application Status "${status}" — falling back to SUBMITTED_CONFIG. Add a mapping or alias for this status.`);
  return SUBMITTED_CONFIG;
}
